import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney, fmtDate } from "@/lib/format";
import {
  ArrowDown,
  ArrowUp,
  CaretDown,
  CaretRight,
} from "@phosphor-icons/react";
import VoucherFormDrawer from "@/components/VoucherFormDrawer";

/**
 * Compact "Linked Vouchers" panel for a sale or booking row.
 *
 *  context: "sale" | "booking"
 *  record: the sale or booking object (needs at least id, branch_id, customer, customer_id)
 *  defaultCollapsed: hide rows by default until expanded
 *
 * Renders:
 *   - a toggle chip "Vouchers (n)"
 *   - on expand: stacked rows of linked vouchers (date, no, kind, method, amount)
 *   - + Receipt and + Payment buttons that open VoucherFormDrawer pre-filled with
 *     the sale/booking customer + branch + related_*_id linkage.
 */
export default function RelatedVouchers({ context, record, defaultCollapsed = true }) {
  const [open, setOpen] = useState(!defaultCollapsed);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ receipts: 0, payments: 0 });
  const [loaded, setLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formKind, setFormKind] = useState("receipt");

  const params =
    context === "sale"
      ? { related_sale_id: record.id }
      : { related_booking_id: record.id };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/vouchers", { params });
      setRows(data.rows || []);
      setTotals(data.totals || { receipts: 0, payments: 0 });
      setLoaded(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, record.id]);

  useEffect(() => {
    if (!loaded) load();
  }, [loaded, load]);

  const preset =
    context === "sale"
      ? {
          branch_id: record.branch_id || "",
          party_type: "customer",
          party_id: record.customer_id,
          party_name: record.customer?.name || "",
          related_sale_id: record.id,
        }
      : {
          branch_id: record.branch_id || "",
          party_type: "customer",
          party_id: record.customer_id,
          party_name: record.customer?.name || "",
          related_booking_id: record.id,
        };

  const handleNew = (kind) => {
    setFormKind(kind);
    setShowForm(true);
  };

  const handleSaved = () => {
    setShowForm(false);
    load();
  };

  const summary =
    rows.length === 0
      ? "no vouchers"
      : `${rows.length} · +${fmtMoney(totals.receipts)} / −${fmtMoney(totals.payments)}`;

  return (
    <div
      data-testid={`related-vouchers-${context}-${record.id}`}
      className="border border-canvas-line bg-canvas-panel/40"
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid={`toggle-related-vouchers-${record.id}`}
          className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted hover:text-ink"
        >
          {open ? (
            <CaretDown size={10} weight="bold" />
          ) : (
            <CaretRight size={10} weight="bold" />
          )}
          Vouchers ({rows.length})
          <span className="text-ink-subtle ml-1">· {summary}</span>
        </button>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => handleNew("receipt")}
            data-testid={`add-receipt-voucher-${record.id}`}
            className="px-2 py-1 border border-[#00B341] text-[#00B341] text-[10px] font-mono uppercase tracking-wider hover:bg-[#00B341] hover:text-white flex items-center gap-1"
            title="Add receipt voucher"
          >
            <ArrowDown size={9} weight="bold" />
            Receipt
          </button>
          <button
            type="button"
            onClick={() => handleNew("payment")}
            data-testid={`add-payment-voucher-${record.id}`}
            className="px-2 py-1 border border-destructive text-destructive text-[10px] font-mono uppercase tracking-wider hover:bg-destructive hover:text-white flex items-center gap-1"
            title="Add payment voucher"
          >
            <ArrowUp size={9} weight="bold" />
            Payment
          </button>
        </div>
      </div>

      {open && rows.length > 0 && (
        <ul className="border-t border-canvas-line">
          {rows.map((v) => (
            <li
              key={v.id}
              data-testid={`related-voucher-${v.id}`}
              className="px-3 py-1.5 border-b border-canvas-line last:border-b-0 flex items-center justify-between gap-2 text-xs"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {v.kind === "receipt" ? (
                  <ArrowDown size={11} weight="bold" className="text-[#00B341] shrink-0" />
                ) : (
                  <ArrowUp size={11} weight="bold" className="text-destructive shrink-0" />
                )}
                <span className="font-mono text-[10px] font-semibold">{v.voucher_no}</span>
                <span className="font-mono text-[10px] text-ink-subtle">
                  {fmtDate(v.date)}
                </span>
                <span className="font-mono text-[10px] uppercase text-ink-muted">
                  {v.payment_method}
                </span>
                {v.narration && (
                  <span className="text-ink-subtle truncate">· {v.narration}</span>
                )}
              </div>
              <span
                className={`font-mono tabular-nums font-semibold ${
                  v.kind === "receipt" ? "text-[#00B341]" : "text-destructive"
                }`}
              >
                {v.kind === "receipt" ? "+" : "−"} {fmtMoney(v.amount)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <VoucherFormDrawer
          open
          defaultKind={formKind}
          preset={preset}
          onClose={() => setShowForm(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
